import React from "react"
import { Button, DatePicker, Checkbox } from "antd"
import { DeleteOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { StaffDaySelector } from "./StaffDaySelector"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import styles from "./SeasonAvailability.module.css"
import IconWithBg from "ui/IconWithBg"

export const SeasonAvailability = ({ seasons, setSeasons }) => {
  const [t] = useTranslation()

  const getDisabledDateForSeason = (currentSeasonId) => {
    return (current) => {
      if (!current) return false
      if (current < dayjs().startOf("day")) {
        return true
      }
      const otherSeasons = seasons.filter((s) => s.id !== currentSeasonId && !s._destroy && s.from && s.to)
      for (const season of otherSeasons) {
        const fromDate = dayjs(season.from, "DD/MM/YYYY")
        const toDate = dayjs(season.to, "DD/MM/YYYY")

        if (current >= fromDate && current <= toDate) {
          return true
        }
      }
      return false
    }
  }

  const addSeason = () => {
    const newSeason = {
      id: Date.now(),
      from: null,
      to: null,
      recurringYearly: false,
      selectedDays: []
    }
    setSeasons([...seasons, newSeason])
  }

  const removeSeason = (seasonId) => {
    setSeasons(
      seasons
        .map((season) => {
          if (season.id === seasonId) {
            const hasRealServerId = season.id && typeof season.id === "number" && season.id < 1000000000
            if (hasRealServerId) {
              return { ...season, _destroy: true }
            }
            return null
          }
          return season
        })
        .filter((season) => season !== null)
    )
  }

  const updateSeason = (seasonId, field, value) => {
    setSeasons(seasons.map((season) => (season.id === seasonId ? { ...season, [field]: value } : season)))
  }

  const visibleSeasons = seasons.filter((season) => !season._destroy)

  return (
    <div className={styles.seasonsContainer}>
      {visibleSeasons.map((season) => (
        <div key={season.id} className={styles.seasonCard}>
          <div className={styles.seasonHeader}>
            <Text variant="heading" fw={500}>
              {t("staff.add_staff_modal.season_availability.title")}
            </Text>
            {visibleSeasons.length > 1 && <IconWithBg icon={<DeleteOutlined />} onClick={() => removeSeason(season.id)} />}
          </div>
          <div className={styles.seasonContent}>
            <div className={styles.dateRow}>
              <div className={styles.dateField}>
                <Text variant="sub" className={styles.label}>
                  {t("staff.add_staff_modal.season_availability.from")}
                </Text>
                <DatePicker
                  size="large"
                  value={season.from ? dayjs(season.from, "DD/MM/YYYY") : null}
                  onChange={(date) => updateSeason(season.id, "from", date ? date.format("DD/MM/YYYY") : null)}
                  format="DD/MM/YYYY"
                  placeholder={t("staff.add_staff_modal.season_availability.from_placeholder")}
                  className={styles.datePicker}
                  suffixIcon={<Icon type="feather" name="Calendar" width={16} height={16} />}
                  disabledDate={getDisabledDateForSeason(season.id)}
                />
              </div>
              <div className={styles.dateField}>
                <Text variant="sub" className={styles.label}>
                  {t("staff.add_staff_modal.season_availability.to")}
                </Text>
                <DatePicker
                  size="large"
                  value={season.to ? dayjs(season.to, "DD/MM/YYYY") : null}
                  onChange={(date) => updateSeason(season.id, "to", date ? date.format("DD/MM/YYYY") : null)}
                  format="DD/MM/YYYY"
                  placeholder={t("staff.add_staff_modal.season_availability.to_placeholder")}
                  className={styles.datePicker}
                  suffixIcon={<Icon type="feather" name="Calendar" width={16} height={16} />}
                  disabledDate={getDisabledDateForSeason(season.id)}
                />
              </div>
            </div>
            <div className={styles.checkboxRow}>
              <Checkbox checked={season.recurringYearly} onChange={(e) => updateSeason(season.id, "recurringYearly", e.target.checked)}>
                <Text variant="body">{t("staff.add_staff_modal.season_availability.recurring_yearly")}</Text>
              </Checkbox>
            </div>
            <div className={styles.daysSection}>
              <Text variant="body" className={styles.questionText}>
                {t("staff.add_staff_modal.days_times_question")}
              </Text>
              <StaffDaySelector
                selectedDays={season.selectedDays}
                setSelectedDays={(days) => updateSeason(season.id, "selectedDays", days)}
              />
            </div>
          </div>
        </div>
      ))}
      <Button type="primary" onClick={addSeason} size="large" className={styles.addSeasonButton}>
        {t("staff.add_staff_modal.season_availability.add_seasonal_availability")}
      </Button>
    </div>
  )
}

SeasonAvailability.propTypes = {
  seasons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      from: PropTypes.string,
      to: PropTypes.string,
      recurringYearly: PropTypes.bool,
      selectedDays: PropTypes.arrayOf(PropTypes.string)
    })
  ).isRequired,
  setSeasons: PropTypes.func.isRequired
}

export default SeasonAvailability
